// BUILD-046 pass F1 (Owner direction 2026-10-25): the Status choice of Assigned by Me, over the REQUESTS the requester issued.
//
// A request is one row. Its lifecycle comes from the rules the page already has, never from a guess:
//   Single / Anyone   the task itself: cancelled or ended (lifecycle, outcome) -> ended; Completed -> completed; Active, In progress, Waiting, Blocked -> open.
//   Everyone          the SERVER'S figure for the whole request (n done of m required, recipients removed or cancelled apart): a cancelled request, or one whose recipients were all removed or
//                     cancelled (m = 0), is ENDED and is never called Completed; every required recipient done (allDone) -> completed; otherwise OPEN, even when some recipients are done.
//                     The readable member list is NOT used to decide completion: it can be partial or restricted.
//   not loaded / unavailable / unreadable   unknown: it is kept by All open and All statuses (it cannot be shown to be finished) and by no other choice.
// An individual status (Active, In progress, Waiting, Blocked) or Overdue keeps an OPEN request that contains a live recipient task in that state; an Everyone request stays together and
// `matchingMemberNames` names the recipients that matched. Completed keeps a request only when it is completed as a whole.
// Pure functions; type-only imports plus the pure overdue rule. Erasable TypeScript.
import type { IssuedMember, IssuedRow } from './issuedRequestsModel';
import { assignmentEnded, isOpenStatus, isOverdueTask } from './overdueRule';
import { followUpConditionOf, followUpDueOn } from './attentionModel';
import { personDisplay } from './displayNames';
import type { IssuedStatusChoice } from './statusChoiceModel';
import { ISSUED_STATUS_OPTIONS } from './statusChoiceModel';

export type IssuedRequestState = 'open' | 'completed' | 'ended' | 'unknown';

export function issuedRequestState(row: IssuedRow): IssuedRequestState {
  if (row.status !== 'ready') return 'unknown';
  if (row.kind === 'set') {
    if (row.requestState === 'cancelled') return 'ended';
    const figure = row.figure;
    if (figure === null) return 'unknown';
    if (figure.setCancelled || figure.m === 0) return 'ended';
    return figure.allDone ? 'completed' : 'open';
  }
  const task = row.task;
  if (task === null) return 'unknown';
  if (assignmentEnded(task)) return 'ended';
  if (task.status === 'completed') return 'completed';
  return isOpenStatus(task.status) ? 'open' : 'unknown';
}

const liveMembers = (row: IssuedRow): readonly IssuedMember[] => row.members.filter((member) => !member.ended && member.status !== null);

function memberMatches(member: IssuedMember, choice: IssuedStatusChoice, todayIso: unknown): boolean {
  if (choice === 'overdue') return isOverdueTask({ status: member.status, dueDate: member.dueDate, redacted: member.restricted, outcome: member.outcome }, todayIso);
  // BUILD-046 G4 (October 23 addendum, items 2 and 4): Follow-up due is judged on THIS recipient's own status and follow-up date, so one recipient's follow-up (or one recipient's finish) never speaks for the request.
  if (choice === 'follow-up-due') return typeof todayIso === 'string' && followUpDueOn({ status: member.status, followUpDate: member.followUpDate, redacted: member.restricted, outcome: member.outcome }, todayIso);
  return member.status === choice;
}

/** True when the request is kept by the choice (see the header for the rule of each choice). */
export function issuedMatchesStatus(row: IssuedRow, choice: IssuedStatusChoice, todayIso: unknown): boolean {
  if (choice === 'all') return true;
  const state = issuedRequestState(row);
  if (choice === 'open') return state === 'open' || state === 'unknown';
  if (choice === 'completed') return state === 'completed';
  if (state !== 'open') return false;
  if (row.kind === 'set') return liveMembers(row).some((member) => memberMatches(member, choice, todayIso));
  const task = row.task;
  if (task === null) return false;
  if (choice === 'follow-up-due') return typeof todayIso === 'string' && followUpDueOn(task, todayIso);
  return choice === 'overdue' ? isOverdueTask(task, todayIso) : task.status === choice;
}

export function filterIssuedByStatus(rows: readonly IssuedRow[], choice: IssuedStatusChoice, todayIso: unknown): IssuedRow[] {
  return choice === 'all' ? [...rows] : rows.filter((row) => issuedMatchesStatus(row, choice, todayIso));
}

export interface IssuedFollowUpNote {
  /** The words a row shows beside the title, in both densities. */
  text: string;
  /** The same sentence with each person's full name, for the hover and focus text. */
  title: string;
}

/**
 * BUILD-046 G4 (October 23 addendum, items 3 and 4): what Assigned by Me says about follow-ups that are DUE on an open request, and for WHOM. A single or queue request names its follow-up (missed with its date, or today) and the person doing it;
 * an Everyone request names each recipient whose OWN follow-up is due, with its own state, so one recipient's follow-up (or one recipient finishing) never stands for the request: it is judged on each live recipient's own status and date. Null when no
 * follow-up is due, when the request is not open or not loaded, and for a recipient shown in the restricted form (their dates are not read). It reads the follow-up date only; the deadline is the Due column's.
 */
export function issuedFollowUpNote(row: IssuedRow, todayIso: unknown, formatDate: (date: string) => string): IssuedFollowUpNote | null {
  if (typeof todayIso !== 'string' || issuedRequestState(row) !== 'open') return null;
  const nameOf = (name: string, form: 'short' | 'full'): string => {
    const person = personDisplay(name);
    const shown = form === 'short' ? person.short : person.full;
    return shown !== '' ? shown : name.trim();
  };
  const state = (condition: 'missed' | 'today', date: string): string => (condition === 'missed' ? `missed ${formatDate(date)}` : 'today');
  if (row.kind === 'set') {
    const due = liveMembers(row).flatMap((member) => {
      const condition = followUpConditionOf({ status: member.status, followUpDate: member.followUpDate, redacted: member.restricted, outcome: member.outcome }, todayIso);
      return condition === null ? [] : [{ member, condition }];
    });
    if (due.length === 0) return null;
    const shown = due.slice(0, 3);
    const more = due.length - shown.length;
    const text = `Follow-up due: ${shown.map(({ member, condition }) => `${nameOf(member.displayName, 'short')} (${state(condition, member.followUpDate)})`).join(', ')}${more > 0 ? ` and ${more} more` : ''}`;
    const title = `Follow-up due: ${due.map(({ member, condition }) => `${nameOf(member.displayName, 'full')} (${state(condition, member.followUpDate)})`).join(', ')}`;
    return { text, title };
  }
  const task = row.task;
  if (task === null) return null;
  const condition = followUpConditionOf(task, todayIso);
  if (condition === null) return null;
  const head = condition === 'missed' ? `Follow-up missed ${formatDate(task.followUpDate)}` : 'Follow-up today';
  const owner = task.unclaimed || task.ownerName.trim() === '' ? '' : task.ownerName;
  return owner === '' ? { text: head, title: head } : { text: `${head} (${nameOf(owner, 'short')})`, title: `${head} (${nameOf(owner, 'full')})` };
}

/** The recipients of an Everyone request that the choice matched (an individual status or Overdue); none for any other choice, request or state. Their stored names, in the list's order. */
export function matchingMemberNames(row: IssuedRow, choice: IssuedStatusChoice, todayIso: unknown): string[] {
  if (row.kind !== 'set' || choice === 'all' || choice === 'open' || choice === 'completed') return [];
  if (issuedRequestState(row) !== 'open') return [];
  return liveMembers(row).filter((member) => memberMatches(member, choice, todayIso)).map((member) => member.displayName);
}

/** How many loaded requests each choice keeps; they describe the LOADED requests, which is a lower bound while more pages remain. */
export function countIssuedByStatus(rows: readonly IssuedRow[], todayIso: unknown): Readonly<Record<IssuedStatusChoice, number>> {
  const out = {} as Record<IssuedStatusChoice, number>;
  for (const option of ISSUED_STATUS_OPTIONS) out[option.value] = rows.filter((row) => issuedMatchesStatus(row, option.value, todayIso)).length;
  return out;
}

/**
 * What an empty list says when the Status choice (not the search) left no request. While more pages remain it never claims there are none: it says how many were loaded and that Load more
 * keeps looking.
 */
export function describeIssuedStatusEmpty(choice: IssuedStatusChoice, loaded: number, complete: boolean): string {
  const label = ISSUED_STATUS_OPTIONS.find((option) => option.value === choice)?.label ?? 'matching';
  if (choice === 'follow-up-due') {
    return complete
      ? 'No requests have a follow-up due.'
      : `None of the ${loaded} ${loaded === 1 ? 'request' : 'requests'} loaded so far ${loaded === 1 ? 'has' : 'have'} a follow-up due. Load more to keep looking.`;
  }
  const words = choice === 'open' ? 'open' : choice === 'overdue' ? 'overdue' : label.toLowerCase();
  if (!complete) return `None of the ${loaded} ${loaded === 1 ? 'request' : 'requests'} loaded so far ${loaded === 1 ? 'is' : 'are'} ${words}. Load more to keep looking.`;
  return choice === 'open' ? 'No open requests. Choose Completed or All statuses in Status to see finished ones.' : `No ${words} requests.`;
}
